import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

// Without this, tailwind-merge reads custom sizes like `text-compact` / `border-hairline` as a color and drops them next to `text-grey-900` / `border-grey-300`.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['compact', 'greeting', 'greeting-lg'] }],
      'border-w': [{ border: ['hairline'] }],
      rounded: [{ rounded: ['nav', 'section'] }],
      // So `sm:max-w-panel` replaces the Sheet's default `sm:max-w-sm` instead of losing to it in CSS order.
      'max-w': [{ 'max-w': ['search', 'chat', 'wide', 'panel'] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatTimeWithAMPM = (date: Date | null): string => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if(!date){
    return '';
  }
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const day = days[date.getDay()];
  const minutesStr = minutes.toString().padStart(2, '0');
  return `${day} ${displayHours}:${minutesStr} ${ampm}`;
};

export function parseBackendUtc(value?: string | null): Date {
  if (!value) return new Date();
  const hasTimezone = /[zZ]|[+-]\d{2}:\d{2}$/.test(value);
  const normalized = hasTimezone ? value : `${value}Z`;
  return new Date(normalized);
}

export function formatFullDate(date: Date): string {
  return date.toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/**
 * Copies text to the clipboard.
 * On HTTP, uses a synchronous `copy` event fallback (call directly from a click handler).
 */
export async function copyTextToClipboard(text: string): Promise<void> {
  const useAsyncClipboard =
    typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText && window.isSecureContext;

  if (!useAsyncClipboard) {
    copyTextWithCopyEvent(text);
    return;
  }

  try {
    await navigator.clipboard.writeText(text);
  } catch {
    copyTextWithCopyEvent(text);
  }
}

function copyTextWithCopyEvent(text: string): void {
  const onCopy = (event: ClipboardEvent) => {
    event.clipboardData?.setData('text/plain', text);
    event.preventDefault();
  };

  document.addEventListener('copy', onCopy, true);

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.cssText =
    'position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:none;outline:none;opacity:0;';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  let ok = false;
  try {
    ok = document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
    document.removeEventListener('copy', onCopy, true);
  }

  if (!ok) {
    throw new Error('Copy failed');
  }
}

/** Copy exactly what is shown in a link/text input (works on HTTP). */
export function copyTextFromInputElement(element: HTMLInputElement | HTMLTextAreaElement): void {
  const text = element.value;
  if (!text) {
    throw new Error('Nothing to copy');
  }

  const onCopy = (event: ClipboardEvent) => {
    event.clipboardData?.setData('text/plain', text);
    event.preventDefault();
  };

  document.addEventListener('copy', onCopy, true);

  let ok = false;
  try {
    element.focus();
    element.select();
    element.setSelectionRange(0, text.length);
    ok = document.execCommand('copy');
  } finally {
    document.removeEventListener('copy', onCopy, true);
  }

  if (!ok) {
    throw new Error('Copy failed');
  }
}

export function formatDate(date?: Date | string | null, short = false): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '-';
  if (short) {
    return d.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
    });
  }
  return d.toLocaleDateString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
